import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Card, Row, Col, Typography, Button, Tag, Table, DatePicker, Select, Empty } from 'antd'
import { PageHeader } from '../../design-system/PageHeader'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined, ExportOutlined, BookOutlined } from '@ant-design/icons'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import axios from 'axios'
import logger from '@vacationtracker/shared/functions/logger'
import { useIntl } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'
import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { PiCheckCircle, PiArrowsClockwise, PiChartBar } from 'react-icons/pi'

import { AddonsStatus, AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { IAddonsData } from '@vacationtracker/shared/types/automations'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

interface IZapierAddonViewProps {
  status?: AddonsStatus
  handleSubscribe?: (automationType: string, isActive: boolean) => void
  addonDetails?: IAddonsData
  currentPlan?: SubscriptionPlan
  authUserId?: string
}

type IntegrationEventStatus = 'pending' | 'processing' | 'succeeded' | 'failed'

interface IIntegrationEvent {
  companyId: string
  integrationType: string
  id: string
  created: string
  modified: string
  type: string
  status: IntegrationEventStatus
  attempt: number
  payload?: any
  response?: any
}

interface IPaginatedIntegrationEvents {
  items: IIntegrationEvent[]
  lastEvaluatedKey?: string
}

interface IAuditFilters {
  from?: string
  to?: string
  status?: IntegrationEventStatus
}

const STATUS_TAG_CONFIG: Record<IntegrationEventStatus, { color: string; darkColor: string; background: string; darkBackground: string; i18nKey: string }> = {
  succeeded: { color: '#15803d', darkColor: '#4ade80', background: '#f0fdf4', darkBackground: '#14532d33', i18nKey: 'addons.adp.audit.statusSucceeded' },
  failed: { color: '#b91c1c', darkColor: '#f87171', background: '#fef2f2', darkBackground: '#7f1d1d33', i18nKey: 'addons.adp.audit.statusFailed' },
  pending: { color: '#92400e', darkColor: '#fbbf24', background: '#fffbeb', darkBackground: '#78350f33', i18nKey: 'app.pending' },
  processing: { color: '#1d4ed8', darkColor: '#60a5fa', background: '#eff6ff', darkBackground: '#1e3a5f33', i18nKey: 'addons.adp.audit.statusProcessing' },
}

const AUDIT_PAGE_SIZE = 10

const { Title, Paragraph, Text } = Typography

const ZapierAddonView = ({ status }: IZapierAddonViewProps) => {
  const { formatMessage } = useIntl()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME
  const [refreshing, setRefreshing] = useState(false)
  const [auditEvents, setAuditEvents] = useState<IIntegrationEvent[]>([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditTotalCount, setAuditTotalCount] = useState<number>(0)
  const [auditFilters, setAuditFilters] = useState<IAuditFilters>({})

  const isConnected = status === AddonsStatusEnum.ACTIVE || status === AddonsStatusEnum.GRANT_PERIOD

  const hasActiveFilters = !!(auditFilters.from || auditFilters.to || auditFilters.status)

  useEffect(() => {
    if (isConnected) {
      fetchAuditEvents()
    }
  }, [isConnected])

  const fetchAuditEvents = useCallback(
    async (filters?: IAuditFilters) => {
      setAuditLoading(true)
      try {
        const tokens = await getAuthTokens()
        const activeFilters = filters ?? auditFilters
        const params: Record<string, string> = { limit: '100' }
        if (activeFilters.from) params.from = activeFilters.from
        if (activeFilters.to) params.to = activeFilters.to
        if (activeFilters.status) params.status = activeFilters.status

        const response = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/zapier/events`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens?.idToken}`,
          },
          params,
        })) as { data: IPaginatedIntegrationEvents }

        setAuditEvents(response.data.items)

        if (activeFilters.from || activeFilters.to || activeFilters.status) {
          if (auditTotalCount === 0) {
            const totalResponse = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/zapier/events`, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokens?.idToken}`,
              },
              params: { limit: '100' },
            })) as { data: IPaginatedIntegrationEvents }
            setAuditTotalCount(totalResponse.data.items.length)
          }
        } else {
          setAuditTotalCount(response.data.items.length)
        }
      } catch (error) {
        logger.error('Failed to fetch audit events', error)
        setAuditEvents([])
      } finally {
        setAuditLoading(false)
      }
    },
    [auditFilters, auditTotalCount]
  )

  const handleAuditFilterChange = (key: keyof IAuditFilters, value: string | undefined) => {
    const newFilters = { ...auditFilters, [key]: value || undefined }
    setAuditFilters(newFilters)
    fetchAuditEvents(newFilters)
  }

  const handleClearFilters = () => {
    setAuditFilters({})
    fetchAuditEvents({})
  }

  const handleRefreshStatus = () => {
    setRefreshing(true)
    window.location.reload()
  }

  const renderNotConnectedView = () => {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Hero Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={24} align="top">
            <Col xs={24} md={16}>
              <Title level={4} style={{ marginBottom: 8 }}>
                <IntlMessages id="addons.zapier.hero.title" />
              </Title>
              <Paragraph style={{ color: isDark ? '#AFBACA' : '#555', lineHeight: 1.65, marginBottom: 16 }}>
                <IntlMessages id="addons.zapier.hero.description" />
              </Paragraph>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiCheckCircle size={14} /> <IntlMessages id="addons.zapier.hero.pill1" />
                </Tag>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiArrowsClockwise size={14} /> <IntlMessages id="addons.zapier.hero.pill2" />
                </Tag>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiChartBar size={14} /> <IntlMessages id="addons.zapier.hero.pill3" />
                </Tag>
              </div>
            </Col>
          </Row>
        </div>

        {/* Setup Guide Heading */}
        <Title level={4} style={{ marginTop: 28, marginBottom: 4 }}>
          <IntlMessages id="addons.zapier.setup.heading" />
        </Title>
        <Paragraph style={{ color: isDark ? '#6B7280' : '#8c8c8c', marginBottom: 20 }}>
          <IntlMessages id="addons.zapier.setup.subheading" />
        </Paragraph>

        {/* Step 1: Create a Zap */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              1
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.zapier.step1.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.zapier.step1.description" />
              </Text>
              <div style={{ marginTop: 12 }}>
                <Button type="primary" icon={<ExportOutlined />} href="https://zapier.com/apps/vacation-tracker/integrations" target="_blank" rel="noopener noreferrer">
                  <IntlMessages id="addons.zapier.step1.button" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 2: Authorize account */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              2
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.zapier.step2.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.zapier.step2.description" />
              </Text>
            </div>
          </div>
        </Card>

        {/* Step 3: Verify the connection */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              3
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.zapier.step3.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.zapier.step3.description" />
              </Text>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button type="primary" icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefreshStatus}>
                  <IntlMessages id="addons.zapier.step3.button" />
                </Button>
                <a href="https://vacationtracker.io/developers/zapier" target="_blank" rel="noopener noreferrer">
                  <Text style={{ fontSize: 13, color: '#7f00ff' }}>
                    <IntlMessages id="addons.zapier.step3.helpLink" />
                  </Text>
                </a>
              </div>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const auditColumns: ColumnsType<IIntegrationEvent> = [
    {
      title: formatMessage({ id: 'addons.adp.audit.column.dateTime' }),
      dataIndex: 'created',
      key: 'created',
      width: 170,
      render: (created: string) => dayjs(created).format('MMM D, YYYY HH:mm'),
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.eventType' }),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag style={{ fontFamily: 'monospace', fontSize: 11 }}>{type}</Tag>,
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.entity' }),
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</Text>,
    },
    {
      title: formatMessage({ id: 'app.status' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (eventStatus: IntegrationEventStatus) => {
        const config = STATUS_TAG_CONFIG[eventStatus]
        return (
          <Tag
            style={{
              color: isDark ? config.darkColor : config.color,
              background: isDark ? config.darkBackground : config.background,
              border: 'none',
              fontWeight: 600,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {formatMessage({ id: config.i18nKey })}
          </Tag>
        )
      },
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.attempt' }),
      dataIndex: 'attempt',
      key: 'attempt',
      width: 80,
      render: (attempt: number) => formatMessage({ id: 'addons.adp.audit.attempt' }, { current: attempt }),
    },
  ]

  const renderConnectedView = () => {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          <IntlMessages id="addons.zapier.audit.title" />
        </Title>

        <Card>
          {/* Filter Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 14,
              paddingBottom: 14,
              borderBottom: `1px solid ${isDark ? '#495862' : '#f0f0f0'}`,
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c' }}>
              <IntlMessages id="app.from" />
            </Text>
            <DatePicker
              size="small"
              value={auditFilters.from ? dayjs(auditFilters.from) : null}
              onChange={(date: Dayjs | null) => handleAuditFilterChange('from', date?.toISOString())}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              style={{ width: 140 }}
            />
            <Text style={{ color: isDark ? '#495862' : '#ccc', fontSize: 12 }}>-</Text>
            <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c' }}>
              <IntlMessages id="app.to" />
            </Text>
            <DatePicker
              size="small"
              value={auditFilters.to ? dayjs(auditFilters.to) : null}
              onChange={(date: Dayjs | null) => handleAuditFilterChange('to', date?.toISOString())}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              style={{ width: 140 }}
            />
            <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c', marginLeft: 8 }}>
              <IntlMessages id="app.status" />
            </Text>
            <Select
              size="small"
              value={auditFilters.status || ''}
              onChange={(value: string) => handleAuditFilterChange('status', value || undefined)}
              style={{ width: 140 }}
              options={[
                { value: '', label: formatMessage({ id: 'addons.adp.audit.statusAll' }) },
                { value: 'succeeded', label: formatMessage({ id: 'addons.adp.audit.statusSucceeded' }) },
                { value: 'failed', label: formatMessage({ id: 'addons.adp.audit.statusFailed' }) },
                { value: 'processing', label: formatMessage({ id: 'addons.adp.audit.statusProcessing' }) },
                { value: 'pending', label: formatMessage({ id: 'app.pending' }) },
              ]}
            />
            {hasActiveFilters && (
              <Button type="link" size="small" onClick={handleClearFilters} style={{ fontSize: 12 }}>
                <IntlMessages id="addons.adp.audit.clearFilters" />
              </Button>
            )}
            {hasActiveFilters && auditEvents.length !== auditTotalCount && (
              <Text style={{ fontSize: 12, color: isDark ? '#6B7280' : '#8c8c8c', marginLeft: 'auto' }}>
                {formatMessage({ id: 'addons.adp.audit.showingCount' }, { filtered: auditEvents.length, total: auditTotalCount })}
              </Text>
            )}
          </div>

          {/* Table */}
          <Table<IIntegrationEvent>
            columns={auditColumns}
            dataSource={auditEvents}
            rowKey={(record) => `${record.id}-${record.created}`}
            loading={auditLoading}
            size="small"
            pagination={{
              pageSize: AUDIT_PAGE_SIZE,
              showSizeChanger: false,
              hideOnSinglePage: auditEvents.length <= AUDIT_PAGE_SIZE,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 11,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    color: isDark ? '#AFBACA' : '#555',
                    background: isDark ? '#1C222B' : '#fafafa',
                    padding: 12,
                    borderRadius: 4,
                  }}
                >
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="addons.adp.audit.payload" />:
                  </Text>
                  {record.payload ? JSON.stringify(record.payload, null, 2) : '-'}
                  <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 4 }}>
                    <IntlMessages id="addons.adp.audit.response" />:
                  </Text>
                  {record.response ? JSON.stringify(record.response, null, 2) : formatMessage({ id: 'addons.adp.audit.responseNotAvailable' })}
                </div>
              ),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={hasActiveFilters ? formatMessage({ id: 'addons.zapier.audit.emptyFiltered' }) : formatMessage({ id: 'addons.zapier.audit.emptyState' })}
                />
              ),
            }}
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="main-content addons-view-page">
      <PageHeader
        title={<IntlMessages id="automations.ZAPIER" />}
        actions={
          <a href="https://vacationtracker.io/developers/zapier" target="_blank" rel="noopener noreferrer">
            <Button type="link" size="small" icon={<BookOutlined />}>
              <IntlMessages id="app.documentation" />
            </Button>
          </a>
        }
        parent={[{ label: <IntlMessages id="app.integrations" />, to: FrontendUrls.integrations }]}
      />
      <div className="main-content-body">{isConnected ? renderConnectedView() : renderNotConnectedView()}</div>
    </div>
  )
}

export default ZapierAddonView
